import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, ChevronDown, Search, Pencil, Trash2 } from 'lucide-react';
import { WebDialog } from '../../common/WebDialog.web';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { ALL_LOCATIONS } from '../../../lib/country-data';
import type { Location } from '@tabbio/types';
import { searchLocations, useDebouncedValue } from '../../../lib/location-source';
import { SkillApplyController } from '../../skills/SkillApplyController.web';

// section / full_cv route through the AI enhance flow (onEnhance). The menu's
// "Apply a skill" item is handled inside SparkButton (opens the Skills picker
// and runs a skill on the CV), so it is intentionally not a SparkScope value.
export type SparkScope = 'section' | 'full_cv';

// ─── WebAITag ───────────────────────────────────────────────────────

interface WebAITagProps {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export function WebAITag({ label, onClick, loading, disabled }: WebAITagProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        height: 28,
        borderRadius: 9999,
        background: hovered ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
        border: 0,
        cursor: disabled || loading ? 'default' : 'pointer',
        opacity: loading ? 0.7 : 1,
        fontFamily: 'inherit',
        transition: 'background-color 150ms, opacity 150ms',
        color: 'var(--text-secondary)',
      }}
    >
      <MaterialSymbol
        name="auto_awesome"
        size={14}
        style={{ color: 'var(--text-secondary)' }}
      />
      <span style={{ fontSize: 12, fontWeight: 500 }}>
        {loading ? 'Generating…' : label}
      </span>
    </button>
  );
}

// ─── SparkButton ────────────────────────────────────────────────────
// The in-editor "Spark" enhance trigger: auto_awesome glyph + label, plus a
// ▾ scope menu (This section / Full CV / Apply a skill). Clicking the main
// button enhances the current section; the menu lets the user widen the scope.
// "Apply a skill" opens the Skills picker and runs a skill on the open CV.

interface SparkButtonProps {
  onEnhance: (scope: SparkScope) => void;
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  /**
   * Hide the "Apply a skill" menu entry: onboarding steps pass false since
   * a persisted CV target does not exist until onboarding is completed.
   */
  showApplySkill?: boolean;
}

export function SparkButton({ onEnhance, loading, disabled, label, showApplySkill = true }: SparkButtonProps) {
  const { t } = useTranslation('seeker');
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDisabled = Boolean(disabled || loading);

  useEffect(() => {
    if (!menuOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [menuOpen]);

  const resolvedLabel = label ?? t('cv.edit.spark.enhance', { defaultValue: 'Enhance' });
  const pick = (scope: SparkScope) => {
    setMenuOpen(false);
    onEnhance(scope);
  };

  const menuItemStyle = (itemDisabled: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
    padding: '9px 12px',
    border: 'none',
    borderRadius: 10,
    backgroundColor: 'transparent',
    color: itemDisabled ? 'var(--text-tertiary)' : 'var(--text-primary)',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'start',
    fontFamily: 'inherit',
    cursor: itemDisabled ? 'default' : 'pointer',
    opacity: itemDisabled ? 0.6 : 1,
  });

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 28,
          borderRadius: 9999,
          background: hovered ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
          opacity: loading ? 0.7 : 1,
          transition: 'background-color 150ms, opacity 150ms',
        }}
      >
        <button
          type="button"
          onClick={() => onEnhance('section')}
          disabled={isDisabled}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            paddingInline: '10px 6px',
            height: 28,
            background: 'none',
            border: 0,
            cursor: isDisabled ? 'default' : 'pointer',
            fontFamily: 'inherit',
            color: 'var(--text-secondary)',
          }}
        >
          <MaterialSymbol
            name="auto_awesome"
            size={14}
            style={{ color: 'var(--text-secondary)' }}
          />
          <span style={{ fontSize: 12, fontWeight: 500 }}>
            {loading
              ? t('cv.edit.spark.enhancing', { defaultValue: 'Enhancing…' })
              : resolvedLabel}
          </span>
        </button>
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          disabled={isDisabled}
          aria-label={t('cv.edit.spark.scopeMenu', { defaultValue: 'Choose scope' })}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingInline: '4px 8px',
            height: 28,
            background: 'none',
            border: 0,
            cursor: isDisabled ? 'default' : 'pointer',
            color: 'var(--text-secondary)',
          }}
        >
          <ChevronDown
            size={13}
            strokeWidth={2}
            style={{
              transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease',
            }}
          />
        </button>
      </div>

      {menuOpen ? (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            insetInlineEnd: 0,
            zIndex: 40,
            minWidth: 200,
            borderRadius: 14,
            border: '1px solid var(--input-border)',
            backgroundColor: 'var(--bg)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
            padding: 6,
          }}
        >
          <button type="button" onClick={() => pick('section')} style={menuItemStyle(false)}>
            {t('cv.edit.spark.scopeSection', { defaultValue: 'This section' })}
          </button>
          <button type="button" onClick={() => pick('full_cv')} style={menuItemStyle(false)}>
            {t('cv.edit.spark.scopeFullCv', { defaultValue: 'Full CV' })}
          </button>
          {showApplySkill ? (
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                setApplyOpen(true);
              }}
              style={menuItemStyle(false)}
            >
              <span>{t('cv.edit.spark.applySkill', { defaultValue: 'Apply a skill' })}</span>
              <MaterialSymbol
                name="extension"
                size={16}
                style={{ color: 'var(--text-tertiary)' }}
              />
            </button>
          ) : null}
        </div>
      ) : null}

      {applyOpen ? (
        <SkillApplyController onClose={() => setApplyOpen(false)} />
      ) : null}
    </div>
  );
}

// ─── SparkPreview ───────────────────────────────────────────────────
// Accept / reject diff card for a Spark enhance suggestion. Shared by every
// CV section form so the "Use this" / "Keep original" affordance stays identical.

interface SparkPreviewProps {
  text: string;
  onAccept: () => void;
  onReject: () => void;
}

export function SparkPreview({ text, onAccept, onReject }: SparkPreviewProps) {
  const { t } = useTranslation('seeker');
  return (
    <div
      style={{
        width: '100%',
        borderRadius: 16,
        border: '1px solid var(--input-border)',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        backgroundColor: 'rgba(0,0,0,0.03)',
        boxSizing: 'border-box',
        marginTop: 8,
      }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <MaterialSymbol name="auto_awesome" size={13} style={{ color: 'var(--text-tertiary)' }} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: 'var(--text-tertiary)',
            fontFamily: 'inherit',
          }}
        >
          {t('cv.edit.spark.suggestion', { defaultValue: 'AI suggestion' })}
        </span>
      </div>
      <p
        dir="auto"
        style={{
          fontSize: 14,
          lineHeight: '21px',
          color: 'var(--text-primary)',
          margin: 0,
          fontFamily: 'inherit',
          whiteSpace: 'pre-wrap',
        }}
      >
        {text}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onAccept}
          style={{
            flex: 1,
            height: 36,
            borderRadius: 9999,
            border: 'none',
            cursor: 'pointer',
            backgroundColor: 'var(--text-primary)',
            color: 'var(--bg)',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'inherit',
          }}
        >
          {t('cv.edit.spark.useThis', { defaultValue: 'Use this' })}
        </button>
        <button
          type="button"
          onClick={onReject}
          style={{
            flex: 1,
            height: 36,
            borderRadius: 9999,
            border: '1px solid var(--input-border)',
            cursor: 'pointer',
            backgroundColor: 'transparent',
            color: 'var(--text-primary)',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'inherit',
          }}
        >
          {t('cv.edit.spark.keepOriginal', { defaultValue: 'Keep original' })}
        </button>
      </div>
    </div>
  );
}

// ─── Shared Styles ──────────────────────────────────────────────────

// Use the long-form border properties so an error/focus override of
// `borderColor` doesn't fight with the `border` shorthand at render time
// (React's "removing a style property when a conflicting one is set" warning).
const INPUT_BASE: React.CSSProperties = {
  width: '100%',
  height: 52,
  borderRadius: 9999,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--input-border)',
  padding: '0 22px',
  fontSize: 15,
  color: 'var(--text-primary)',
  backgroundColor: 'var(--input-bg)',
  boxShadow: 'var(--gem-input-shadow)',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 150ms',
  boxSizing: 'border-box' as const,
};

const FOCUS_STYLE: React.CSSProperties = {
  borderColor: 'var(--text-primary)',
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  marginBottom: 8,
  display: 'block',
  fontFamily: 'inherit',
};

// ─── WebInput ───────────────────────────────────────────────────────

interface WebInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  /** Focusable/copyable but not editable: for locked identity fields. */
  readOnly?: boolean;
  autoComplete?: string;
  error?: string;
}

export function WebInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled = false,
  readOnly = false,
  autoComplete,
  error,
}: WebInputProps) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ marginTop: 12 }}>
      {label && <label style={LABEL_STYLE}>{label}</label>}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        aria-readonly={readOnly || undefined}
        autoComplete={autoComplete}
        dir={['email', 'url', 'tel', 'password', 'number'].includes(type) ? 'ltr' : 'auto'}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...INPUT_BASE,
          ...(focused && !error && !readOnly ? FOCUS_STYLE : {}),
          ...(error ? { borderColor: 'var(--color-danger, #dc2626)' } : {}),
          ...(readOnly ? { color: 'var(--text-secondary)' } : {}),
          opacity: disabled ? 0.6 : 1,
        }}
      />
      {error ? (
        <span style={{ color: 'var(--color-danger, #dc2626)', fontSize: 12, marginTop: 4, display: 'block', paddingInline: 4 }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

// ─── WebTextarea ────────────────────────────────────────────────────

interface WebTextareaProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  showCharCount?: boolean;
  error?: string;
}

export function WebTextarea({ label, value, onChange, placeholder, rows = 6, showCharCount, error }: WebTextareaProps) {
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // SCRUM-243: on mount, scroll prefilled content to top so first line is visible.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = 0;
  }, []);

  // SCRUM-312: AI-populated value lands after mount; reset scroll on empty → non-empty.
  const prevValueRef = useRef('');
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prevValueRef.current === '' && value !== '') {
      el.scrollTop = 0;
    }
    prevValueRef.current = value;
  }, [value]);

  return (
    <div style={{ marginTop: 12 }}>
      {label && <label style={LABEL_STYLE}>{label}</label>}
      <textarea
        ref={ref}
        dir="auto"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        onFocus={(e) => {
          setFocused(true);
          // Place caret at start of prefilled content instead of end.
          const target = e.currentTarget;
          if (target.value && target.selectionStart === target.value.length) {
            target.setSelectionRange(0, 0);
            target.scrollTop = 0;
          }
        }}
        onBlur={() => setFocused(false)}
        style={{
          ...INPUT_BASE,
          height: 'auto',
          padding: '14px 18px',
          borderRadius: 20,
          minHeight: 140,
          resize: 'vertical' as const,
          lineHeight: '22px',
          ...(focused && !error ? FOCUS_STYLE : {}),
          ...(error ? { borderColor: 'var(--color-danger, #dc2626)' } : {}),
        }}
      />
      {error ? (
        <span style={{ color: 'var(--color-danger, #dc2626)', fontSize: 12, marginTop: 4, display: 'block', paddingInline: 4 }}>
          {error}
        </span>
      ) : null}
      {showCharCount && (
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)', textAlign: 'end', display: 'block', marginTop: 4 }}>
          {value.length} chars
        </span>
      )}
    </div>
  );
}

// ─── WebChipList ────────────────────────────────────────────────────

interface WebChipListProps {
  items: string[];
  onAdd: (value: string) => void;
  onRemove: (index: number) => void;
  placeholder?: string;
  addLabel?: string;
  emptyHint?: string;
  validate?: (value: string) => string | undefined;
  // Controlled-input mode: parents that need to flush a pending entry on save
  // pass both `inputValue` and `onInputChange`. Without them the chip list
  // manages the input internally and the pending value is lost when the
  // parent submits.
  inputValue?: string;
  onInputChange?: (value: string) => void;
}

export function WebChipList({ items, onAdd, onRemove, placeholder, addLabel, emptyHint, validate, inputValue, onInputChange }: WebChipListProps) {
  const [internalInput, setInternalInput] = useState('');
  const input = inputValue !== undefined ? inputValue : internalInput;
  const setInput = onInputChange ?? setInternalInput;
  const [addHover, setAddHover] = useState(false);
  const trimmed = input.trim();
  const error = trimmed && validate ? validate(trimmed) : undefined;
  const canAdd = trimmed.length > 0 && !error;

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd(trimmed);
    setInput('');
  };

  const resolvedAddLabel = addLabel ?? 'Add';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {items.map((item, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 34,
                paddingInline: 14,
                borderRadius: 9999,
                // --hover is theme-aware: the old rgba(0,0,0,0.04) darkened the
                // chip into the page in dark mode, so chips read as missing.
                backgroundColor: 'var(--hover)',
                color: 'var(--text-primary)',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'inherit',
              }}
            >
              <span dir="auto">{item}</span>
              <button
                onClick={() => onRemove(i)}
                aria-label={`Remove ${item}`}
                style={{ display: 'flex', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-tertiary)' }}
              >
                <X size={12} strokeWidth={2} />
              </button>
            </span>
          ))}
        </div>
      ) : emptyHint ? (
        <div style={{ fontSize: 13, color: 'var(--text-tertiary)', paddingBlock: 4 }}>
          {emptyHint}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          dir="auto"
          aria-label={placeholder ?? resolvedAddLabel}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          style={{
            ...INPUT_BASE,
            flex: 1,
            padding: '10px 16px',
            fontSize: 14,
            ...(error ? { borderColor: 'var(--color-danger, #dc2626)' } : {}),
          }}
        />
        <button
          onClick={handleAdd}
          disabled={!canAdd}
          onMouseEnter={() => setAddHover(true)}
          onMouseLeave={() => setAddHover(false)}
          aria-label={resolvedAddLabel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            height: 40,
            paddingInline: 16,
            borderRadius: 9999,
            border: 'none',
            cursor: canAdd ? 'pointer' : 'not-allowed',
            backgroundColor: 'var(--text-primary)',
            color: 'var(--bg)',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'inherit',
            flexShrink: 0,
            opacity: canAdd ? 1 : 0.5,
            filter: addHover && canAdd ? 'brightness(1.15)' : 'brightness(1)',
            transition: 'filter 150ms, opacity 150ms',
          }}
        >
          <Plus size={16} strokeWidth={2} />
          {resolvedAddLabel}
        </button>
      </div>
      {error ? (
        <span style={{ color: 'var(--color-danger, #dc2626)', fontSize: 12, paddingInline: 4 }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

// ─── WebLocationChipList ────────────────────────────────────────────

interface WebLocationChipListProps {
  items: string[];
  onAdd: (value: string) => void;
  onRemove: (index: number) => void;
  placeholder?: string;
}

export function WebLocationChipList({
  items,
  onAdd,
  onRemove,
  placeholder,
}: WebLocationChipListProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  const selectedSet = useMemo(
    () => new Set(items.map((item) => item.toLowerCase())),
    [items],
  );

  const [results, setResults] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebouncedValue(search, 250);
  const abortRef = useRef<AbortController | null>(null);

  const userTyped = search.trim().length > 0;

  useEffect(() => {
    const q = debouncedSearch.trim();
    abortRef.current?.abort();
    // Clear immediately so the user never sees rows from a previous query
    // while the next fetch is in flight.
    setResults([]);
    if (q.length < 2) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    searchLocations(q, { signal: controller.signal })
      .then((res) => {
        if (!controller.signal.aborted) {
          setResults(res);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setResults([]);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [debouncedSearch]);

  const isStale = debouncedSearch !== search;

  const filteredOptions = useMemo(() => {
    if (!userTyped) return ALL_LOCATIONS.slice(0, 80);
    if (isStale || loading) return [];
    return results
      .map((r) => r.displayName)
      .filter((loc) => !selectedSet.has(loc.toLowerCase()));
  }, [userTyped, isStale, loading, results, selectedSet]);

  const showLoadingRow = userTyped && (loading || isStale);
  const showNoResultsRow =
    userTyped && !loading && !isStale && filteredOptions.length === 0;

  const selectLocation = (location: string) => {
    if (selectedSet.has(location.toLowerCase())) return;
    onAdd(location);
    setSearch('');
    setOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {items.map((item, i) => (
            <span
              key={`${item}-${i}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '5px 12px',
                borderRadius: 9999,
                backgroundColor: 'rgba(0,0,0,0.04)',
                color: 'var(--text-primary)',
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'inherit',
              }}
            >
              {item}
              <button
                onClick={() => onRemove(i)}
                style={{
                  display: 'flex',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                }}
                aria-label="Remove"
              >
                <X size={12} color="var(--text-primary)" strokeWidth={2} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div ref={containerRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen((value) => !value)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 40,
            paddingInline: 14,
            borderRadius: 9999,
            border: '1px solid var(--input-border)',
            backgroundColor: 'transparent',
            color: 'var(--text-secondary)',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          <Plus size={16} strokeWidth={2} />
          {placeholder ?? 'Add location'}
          <ChevronDown
            size={14}
            strokeWidth={2}
            style={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease',
            }}
          />
        </button>

        {open ? (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              insetInlineStart: 0,
              zIndex: 30,
              minWidth: 280,
              maxWidth: 360,
              borderRadius: 16,
              border: '1px solid var(--input-border)',
              backgroundColor: 'var(--bg)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              padding: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                paddingInline: 10,
                paddingBlock: 6,
                borderRadius: 9999,
                backgroundColor: 'rgba(0,0,0,0.04)',
                marginBottom: 8,
              }}
            >
              <Search size={14} color="var(--text-tertiary)" strokeWidth={2} />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={placeholder ?? 'Search location'}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  fontFamily: 'inherit',
                }}
              />
              {search ? (
                <button
                  onClick={() => setSearch('')}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: 2,
                    display: 'inline-flex',
                  }}
                  aria-label="Clear"
                >
                  <X size={12} color="var(--text-tertiary)" />
                </button>
              ) : null}
            </div>

            <div style={{ height: 280, overflowY: 'auto' }}>
              {filteredOptions.map((location, idx) => {
                const isSelected = selectedSet.has(location.toLowerCase());
                return (
                  <button
                    key={`${location}-${idx}`}
                    onClick={() => selectLocation(location)}
                    disabled={isSelected}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '9px 12px',
                      border: 'none',
                      borderRadius: 10,
                      backgroundColor: 'transparent',
                      color: isSelected
                        ? 'var(--text-tertiary)'
                        : 'var(--text-primary)',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'start',
                      fontFamily: 'inherit',
                      cursor: isSelected ? 'default' : 'pointer',
                      opacity: isSelected ? 0.55 : 1,
                    }}
                  >
                    {location}
                    {isSelected ? (
                      <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        Selected
                      </span>
                    ) : null}
                  </button>
                );
              })}
              {showLoadingRow ? (
                <div
                  style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                  }}
                >
                  Searching…
                </div>
              ) : null}
              {showNoResultsRow ? (
                <div
                  style={{
                    padding: '14px 12px',
                    textAlign: 'center',
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                  }}
                >
                  No matches
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── WebPillSelector ────────────────────────────────────────────────

interface WebPillSelectorProps {
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
}

export function WebPillSelector({ options, selected, onToggle }: WebPillSelectorProps) {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((opt) => {
        const isActive = selected.includes(opt);
        const isHover = hovered === opt;
        return (
          <button
            key={opt}
            onClick={() => onToggle(opt)}
            onMouseEnter={() => setHovered(opt)}
            onMouseLeave={() => setHovered(null)}
            style={{
              padding: '8px 16px',
              borderRadius: 9999,
              border: isActive ? '1px solid var(--text-primary)' : '1px solid var(--input-border)',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
              fontFamily: 'inherit',
              backgroundColor: isActive ? 'var(--text-primary)' : isHover ? 'rgba(0,0,0,0.04)' : 'var(--bg)',
              color: isActive ? 'var(--bg)' : 'var(--text-primary)',
              transition: 'background-color 150ms, border-color 150ms, color 150ms',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ─── WebFormCard ─────────────────────────────────────────────────────

interface WebFormCardProps {
  index?: number;
  onDelete?: () => void;
  children: React.ReactNode;
}

export function WebFormCard({ index, onDelete, children }: WebFormCardProps) {
  const [deleteHover, setDeleteHover] = useState(false);

  return (
    <div
      style={{
        borderRadius: 16,
        backgroundColor: 'rgba(0,0,0,0.04)',
        border: '1px solid var(--input-border)',
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {(index !== undefined || onDelete) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {index !== undefined && (
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)' }}>#{index + 1}</span>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              onMouseEnter={() => setDeleteHover(true)}
              onMouseLeave={() => setDeleteHover(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: deleteHover ? 'rgba(217, 48, 37, 0.08)' : 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 6px',
                borderRadius: 8,
                transition: 'background-color 150ms',
              }}
            >
              <X size={16} color="#d93025" strokeWidth={2} />
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── WebSaveButton ──────────────────────────────────────────────────

interface WebSaveButtonProps {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export function WebSaveButton({ label, onClick, loading, disabled }: WebSaveButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      type="button"
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      style={{
        width: '100%',
        height: 48,
        borderRadius: 9999,
        border: 'none',
        cursor: isDisabled ? 'default' : 'pointer',
        backgroundColor: 'var(--text-primary)',
        color: 'var(--bg)',
        fontSize: 15,
        fontWeight: 500,
        fontFamily: 'inherit',
        marginTop: 16,
        opacity: isDisabled ? 0.5 : 1,
        transition: 'opacity 150ms',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      }}
      onMouseEnter={(e) => {
        if (!isDisabled) e.currentTarget.style.opacity = '0.88';
      }}
      onMouseLeave={(e) => {
        if (!isDisabled) e.currentTarget.style.opacity = '1';
      }}
    >
      {loading && (
        <span
          style={{
            width: 16,
            height: 16,
            border: '2px solid rgba(255,255,255,0.3)',
            borderTopColor: 'var(--bg)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            display: 'inline-block',
          }}
        />
      )}
      {label}
    </button>
  );
}

// ─── WebAddButton ───────────────────────────────────────────────────

interface WebAddButtonProps {
  label: string;
  onClick: () => void;
}

export function WebAddButton({ label, onClick }: WebAddButtonProps) {
  const [hover, setHover] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        height: 44,
        borderRadius: 9999,
        // --gem-outline is a solid, theme-aware outline (#dadce0 light / #3c4043
        // dark): visible in dark mode, unlike the faint --input-border (0.08
        // alpha) which disappeared against the near-black background.
        border: '1px dashed var(--gem-outline)',
        cursor: 'pointer',
        // --hover-strong adapts per theme (dark = white overlay); the old
        // rgba(0,0,0,0.04) darkened instead of lightening in dark mode.
        backgroundColor: hover ? 'var(--hover-strong)' : 'transparent',
        color: 'var(--text-primary)',
        fontSize: 14,
        fontWeight: 500,
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        transition: 'background-color 150ms, border-color 150ms',
      }}
    >
      <Plus size={16} strokeWidth={2} />
      {label}
    </button>
  );
}

// ─── WebToggle ──────────────────────────────────────────────────────

interface WebToggleProps {
  label: string;
  description?: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

export function WebToggle({ label, description, value, onChange }: WebToggleProps) {
  const [hover, setHover] = useState(false);

  return (
    <button
      onClick={() => onChange(!value)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: 16,
        borderRadius: 16,
        border: '1px solid var(--input-border)',
        cursor: 'pointer',
        backgroundColor: hover ? 'rgba(0,0,0,0.04)' : 'var(--bg)',
        textAlign: 'start',
        fontFamily: 'inherit',
        gap: 12,
        transition: 'background-color 150ms',
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
        {description && <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 2 }}>{description}</div>}
      </div>
      <div
        style={{
          width: 48,
          height: 30,
          borderRadius: 15,
          backgroundColor: value ? 'var(--text-primary)' : 'rgba(0,0,0,0.06)',
          border: value ? 'none' : '1px solid var(--input-border)',
          position: 'relative',
          transition: 'background-color 200ms, border-color 200ms',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 12,
            // Issue #133: was `var(--bg)`, which is near-black in dark mode and
            // made the thumb disappear against the track. Native's thumb is
            // always white (`thumbColor="#ffffff"`): match that here.
            backgroundColor: '#ffffff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            position: 'absolute',
            top: 3,
            insetInlineStart: value ? 21 : 3,
            transition: 'inset-inline-start 200ms',
          }}
        />
      </div>
    </button>
  );
}

// ─── WebFormDialog ──────────────────────────────────────────────────

interface WebFormDialogProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export function WebFormDialog({ visible, title, onClose, children, footer }: WebFormDialogProps) {
  return (
    <WebDialog visible={visible} title={title} onClose={onClose} maxWidth={480} footer={footer}>
      {children}
    </WebDialog>
  );
}

// ─── WebItemCard ────────────────────────────────────────────────────

interface WebItemCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onClick: () => void;
  onDelete?: () => void;
}

export function WebItemCard({ title, subtitle, icon, onClick, onDelete }: WebItemCardProps) {
  const [hovered, setHovered] = useState(false);
  const [editHover, setEditHover] = useState(false);
  const [deleteHover, setDeleteHover] = useState(false);

  const handleRowClick = () => onClick();
  const handleRowKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
  };

  const iconBtnStyle = (active: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, borderRadius: 9999, border: 'none',
    background: active ? 'rgba(0,0,0,0.08)' : 'transparent',
    cursor: 'pointer', padding: 0, transition: 'background-color 150ms',
    flexShrink: 0, fontFamily: 'inherit',
  });

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, paddingInlineStart: 16, borderRadius: 16, backgroundColor: hovered ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)', width: '100%', transition: 'background-color 150ms' }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={handleRowClick}
        onKeyDown={handleRowKey}
        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'start' as const, outline: 'none' }}
      >
        {icon && <div style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
          {subtitle && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <button
          type="button"
          onClick={onClick}
          onMouseEnter={() => setEditHover(true)}
          onMouseLeave={() => setEditHover(false)}
          aria-label="Edit"
          style={iconBtnStyle(editHover)}
        >
          <Pencil size={16} color="var(--text-tertiary)" strokeWidth={1.75} />
        </button>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            onMouseEnter={() => setDeleteHover(true)}
            onMouseLeave={() => setDeleteHover(false)}
            aria-label="Delete"
            style={iconBtnStyle(deleteHover)}
          >
            <Trash2 size={16} color="#d93025" strokeWidth={1.75} />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── WebEmptyState ──────────────────────────────────────────────────

export function WebEmptyState({ icon, text }: { icon?: React.ReactNode; text: string }) {
  return (
    <div style={{ padding: '40px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      {icon}
      <div style={{ fontSize: 15, color: 'var(--text-tertiary)' }}>{text}</div>
    </div>
  );
}
